import type { Facing, ItemId } from "@clubap/shared";
import { MIRRORED_FACING } from "@clubap/shared";
import type { AvatarLayer } from "./manifest.js";

/**
 * ==========================================================================
 *  ARTE Y COLOCACIÓN DE LOS ÍTEMS. Lo escribe el panel, no la mano.
 * ==========================================================================
 *
 * Este archivo es GENERADO: lo reescribe el configurador de ítems (panel de
 * cuenta dev, sólo en desarrollo). Se puede editar a mano y no se rompe nada,
 * pero el panel lo va a reordenar la próxima vez que guardes desde ahí.
 *
 * Vive aparte de `manifest.ts` por lo mismo que `artShapes.ts` vive aparte de
 * `rooms.ts`: uno es un archivo autorado, lleno de comentarios que explican
 * decisiones, y el otro es una tabla que una herramienta reescribe entera.
 * Mezclarlos obligaría a hacer cirugía de AST sobre los comentarios para
 * guardar un desplazamiento. `manifest.ts` lo reexporta, así que sigue siendo
 * la puerta de entrada del arte.
 */

/**
 * Dónde cae un dibujo de ítem sobre el cuerpo, en UNA dirección.
 *
 * Hasta ahora un ítem no tenía posición: era una capa más sobre el mismo lienzo
 * de 400x512, y la alineación estaba horneada en el archivo. Eso obliga a
 * exportar cada gorra a lienzo completo y a rehacer el archivo entero para
 * subirla dos píxeles. Con esto el dibujo puede ser pequeño y la posición es
 * dato.
 */
export interface ItemPlacement {
  /**
   * Desplazamiento en unidades del LIENZO DEL AVATAR (400x512), no en píxeles de
   * pantalla: el mundo escala el avatar y el carnet lo recorta, así que unas
   * coordenadas de pantalla sólo valdrían en el sitio donde se ajustaron.
   */
  x: number;
  y: number;
  /** Multiplica la escala compartida. 1 es el tamaño con el que se exportó. */
  scale: number;
  /**
   * Orden de dibujo. Negativo lo mete POR DEBAJO del cuerpo — una capucha o una
   * mochila pasan por detrás de la cabeza.
   *
   * No es lo mismo que la ranura, aunque hasta ahora `ITEM_SLOTS` hacía las dos
   * cosas. La ranura decide EXCLUSIVIDAD (un sombrero reemplaza al que hubiera);
   * esto decide QUÉ TAPA A QUÉ. Un mismo sombrero puede ir delante de cara al
   * sur y detrás de espaldas, y eso es orden, no exclusividad.
   */
  z: number;
}

export interface ItemArt extends AvatarLayer {
  /**
   * Qué trozo del lienzo ocupa el dibujo, en unidades de 400x512.
   *
   * Lo calcula el configurador midiendo el archivo, y existe para que se pueda hacer una
   * MINIATURA. El arte de un ítem se autora sobre el lienzo entero del avatar —para que
   * caiga en su sitio al llevarlo puesto— así que una miniatura del lienzo deja una gorra
   * en siete píxeles. Con la caja se recorta al dibujo y se ve lo que es.
   *
   * Ausente significa "no se sabe": la miniatura enseña el lienzo entero, que es feo pero
   * no miente.
   */
  bbox?: { x: number; y: number; width: number; height: number };
  /**
   * Por dirección, y sólo las que se declaren.
   *
   * Las que falten se derivan igual que el dibujo: si existe su espejo se usa
   * ése con la `x` invertida, y si tampoco, queda neutra. Ojo — la que manda es
   * la dirección de la que salió la TEXTURA, no ésta: ver `placementFor`.
   */
  placement?: Partial<Record<Facing, ItemPlacement>>;
}

/** Ni desplazada, ni escalada, ni reordenada: el ítem tal como se exportó. */
export const NEUTRAL_PLACEMENT: ItemPlacement = { x: 0, y: 0, scale: 1, z: 0 };

/**
 * La colocación de una perspectiva. Ausente es sin tocar.
 *
 * Se indexa por la dirección HACIA DONDE MIRA el jugador, y se aplica TAL CUAL: las
 * ocho se declaran y ninguna se deduce de otra.
 *
 * Hubo dos intentos antes y los dos fallaron por inferir. El primero la leía de la
 * dirección del DIBUJO, así que un ítem con un solo trazo —el caso normal— sólo podía
 * colocarse una vez. El segundo declaraba cinco y espejaba tres invirtiendo la `x`, y
 * eso deja al mundo pintando tres posiciones que el panel no enseña: pones el café en
 * un sitio, giras, y aparece en otro que nunca ajustaste.
 *
 * Espejar sólo sería correcto si el dibujo del ítem se espejara con el cuerpo, y con un
 * dibujo único no lo hace: el trazo no se voltea, sólo salta de lado. Cuesta tres
 * ajustes más por ítem y a cambio lo que se ve en el panel es lo que se dibuja.
 */
export function placementFor(placement: ItemArt["placement"], facing: Facing): ItemPlacement {
  return placement?.[facing] ?? NEUTRAL_PLACEMENT;
}

/** Las direcciones que se autoran; las otras tres salen espejando éstas. */
export const AUTHORED_FACINGS: readonly Facing[] = ["s", "se", "w", "nw", "n"];

/** ¿Esta dirección se declara, o se deriva de otra? Lo usa el panel y el check. */
export function isAuthoredFacing(facing: Facing): boolean {
  return AUTHORED_FACINGS.includes(facing);
}

/** El espejo de una dirección derivada, o null si es de las que se autoran. */
export function mirrorSourceOf(facing: Facing): Facing | null {
  if (isAuthoredFacing(facing)) return null;
  return MIRRORED_FACING[facing] ?? null;
}

/**
 * Arte por ítem. Un ítem que no esté acá no se ve, aunque se posea y se equipe:
 * la economía funciona, sólo falta el dibujo.
 */
/**
 * El arte de un ítem por un id suelto.
 *
 * `ITEM_ART` se tipa por `ItemId` para que la tabla generada no pueda declarar arte de
 * un ítem que no existe. Pero quien la consulta trae el id como string: viene del
 * inventario, que llega por HTTP. Este acceso es el ÚNICO sitio donde se cruza esa
 * frontera, en vez de repartir un `as` por cada pantalla que enseñe un ítem.
 */
export function itemArtOf(id: string): ItemArt | undefined {
  return ITEM_ART[id as ItemId];
}

/* === TABLA GENERADA — desde aquí hasta el final lo reescribe el configurador === */
export const ITEM_ART: Partial<Record<ItemId, ItemArt>> = {
  "gorra-espol": {
    idle: {
      s: "/art/items/gorra-espol/s.svg",
      se: "/art/items/gorra-espol/se.svg",
      n: "/art/items/gorra-espol/n.svg",
      nw: "/art/items/gorra-espol/nw.svg",
      w: "/art/items/gorra-espol/w.svg",
    },
    placement: {
      s: { x: -3, y: -16, scale: 1, z: 0 },
      se: { x: -3, y: -16, scale: 1, z: 0 },
      e: { x: -3, y: -16, scale: 1, z: 0 },
      ne: { x: -3, y: -16, scale: 1, z: 0 },
      n: { x: -3, y: -16, scale: 1, z: 0 },
      nw: { x: -3, y: -16, scale: 1, z: 0 },
      w: { x: -3, y: -16, scale: 1, z: 0 },
      sw: { x: -3, y: -16, scale: 1, z: 0 },
    },
    bbox: { x: 122, y: 0, width: 156, height: 114 },
  },
  lentes: {
    idle: {
      s: "/art/items/lentes/s.svg",
      se: "/art/items/lentes/se.svg",
      n: "/art/items/lentes/n.svg",
      nw: "/art/items/lentes/nw.svg",
      w: "/art/items/lentes/w.svg",
    },
    placement: {
      s: { x: -1, y: 133, scale: 1.35, z: 0 },
      se: { x: -1, y: 133, scale: 1.35, z: 0 },
      e: { x: -1, y: 133, scale: 1.35, z: 0 },
      ne: { x: -1, y: 133, scale: 1.35, z: 0 },
      n: { x: -1, y: 133, scale: 1.35, z: 0 },
      nw: { x: -1, y: 133, scale: 1.35, z: 0 },
      w: { x: -1, y: 133, scale: 1.35, z: 0 },
      sw: { x: -1, y: 133, scale: 1.35, z: 0 },
    },
    bbox: { x: 140, y: 68, width: 120, height: 54 },
  },
  cafe: {
    idle: "/art/items/_test/cafe.svg",
    placement: {
      s: { x: 0, y: 166, scale: 1.44, z: 1 },
      se: { x: 22, y: 180, scale: 1.44, z: 0 },
      e: { x: 77, y: 174, scale: 1.44, z: -1 },
      ne: { x: 63, y: 166, scale: 1.44, z: -1 },
      n: { x: -185, y: 183, scale: 1.44, z: -1 },
      nw: { x: -182, y: 174, scale: 1.44, z: -1 },
      w: { x: -116, y: 183, scale: 1.44, z: 0 },
      sw: { x: 127, y: 177, scale: 1.44, z: 0 },
    },
    bbox: { x: 226, y: 182, width: 62, height: 128 },
  },
  dona: {
    idle: "/art/items/_test/dona.svg",
    placement: {
      s: { x: -22, y: 116, scale: 1, z: 0 },
      se: { x: 36, y: 116, scale: 1, z: 0 },
      e: { x: 102, y: 102, scale: 1, z: 0 },
      ne: { x: 160, y: 105, scale: 1, z: -1 },
      n: { x: 163, y: 113, scale: 1, z: -1 },
      nw: { x: 105, y: 91, scale: 1, z: -1 },
      w: { x: 61, y: 99, scale: 1, z: -1 },
      sw: { x: -36, y: 105, scale: 1, z: 0 },
    },
    bbox: { x: 88, y: 218, width: 88, height: 88 },
  },
  "gorro-lana": {
    idle: "/art/items/_test/gorro-lana.svg",
    placement: {
      s: { x: 0, y: -30, scale: 1, z: 0 },
      se: { x: 0, y: -30, scale: 1, z: 0 },
      e: { x: 0, y: -30, scale: 1, z: 0 },
      ne: { x: 0, y: -30, scale: 1, z: 0 },
      n: { x: 0, y: -30, scale: 1, z: 0 },
      nw: { x: 0, y: -30, scale: 1, z: 0 },
      w: { x: 0, y: -30, scale: 1, z: 0 },
      sw: { x: 0, y: -30, scale: 1, z: 0 },
    },
    bbox: { x: 114, y: 0, width: 172, height: 88 },
  },
};
